import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap, filter, shareReplay } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { Institution, User, roleValueIDs } from 'src/app/models/user';
import { PaymentService } from 'src/app/services/payment.service';
import { UserService } from 'src/app/services/user.service';
import {
  PencilButtonComponent,
  PencilButtonThemeEnum,
} from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import * as moment from 'moment-timezone';
import { SettingsService } from 'src/app/services/settings.service';
import { panelID } from 'src/app/settings/settings.constants';
import { Subscription } from 'src/app/models/payment';
import { AclService } from 'src/app/services/acl.service';
import { isNativeApp } from 'src/app/utilities/native-app.utils';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { PipeModule } from '../../pipes/pipes.module';

@Component({
  selector: 'app-nav-sidebar-upgrade-banner',
  standalone: true,
  imports: [CommonModule, PencilButtonComponent, TranslateModule, PipeModule, MatDialogModule],
  templateUrl: './nav-sidebar-upgrade-banner.component.html',
  styleUrls: ['./nav-sidebar-upgrade-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavSidebarUpgradeBannerComponent {
  protected readonly IconTypes = IconTypes;
  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;

  private readonly user$: Observable<User> = this.userService.user.pipe(
    map((res) => res?.user),
    filter((user): user is User => !!user),
  );

  protected readonly isTrialEligibleGuestUser$ = this.user$.pipe(
    map((user) => this.checkIsTrialEligibleGuestUser(user)),
  );

  protected readonly isTrialInstitutionAdmin$ = this.user$.pipe(
    switchMap((user) => from(this.checkIsTrialInstitutionAdmin(user))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly nextBillingDate$ = this.user$.pipe(
    map((user) => this.getNextBillingDate(user)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private readonly paymentService: PaymentService,
    private readonly settingsService: SettingsService,
    private readonly aclService: AclService,
    private readonly userService: UserService,
    private readonly telemetry: TelemetryService,
  ) {}

  private checkIsTrialEligibleGuestUser(user: User): boolean {
    const role = user.info?.role;
    return (
      !isNativeApp() &&
      !!user.feature_flags?.enable_billing &&
      !user.institution &&
      (role === roleValueIDs.teacher || role === roleValueIDs.admin)
    );
  }

  private async checkIsTrialInstitutionAdmin(user: User): Promise<boolean> {
    const institution = <Institution>user.institution;
    if (isNativeApp() || !user.feature_flags?.enable_billing || !institution) {
      return false;
    }

    const isInstitutionAdmin = this.aclService.isAdmin(user);
    const isTrialInstitution = await this.paymentService.checkIsTrialInstitution(institution);
    return isInstitutionAdmin && isTrialInstitution;
  }

  private getNextBillingDate(user: User): Date | undefined {
    const subscription = <Subscription>user.institution?.billingStatus?.subscription;
    return subscription?.nextBillingAt
      ? moment.unix(subscription.nextBillingAt).toDate()
      : undefined;
  }

  startFreeTrial(): void {
    this.paymentService.openStartTrialModal('nav_sidebar_upgrade_banner');
  }

  openPaymentSettings(): void {
    this.telemetry.logBasicEvent('[Payment Settings Opened for Trial Institution Upgrade]', {
      source: 'nav_sidebar_upgrade_banner',
    });
    this.settingsService.showSettingsPanel(panelID.billableUsage);
  }
}
